// Regras de formato e tamanho da gravação de tela do chat.
//
// Ficam fora do hook pelo mesmo motivo do audioRecorderFormat: são decisões
// puras, e `MediaRecorder` não existe no jsdom.

import { containerDoMime } from './audioRecorderFormat';

// O bucket chat-attachments tem teto de 50MB por arquivo (e o plano do
// Supabase também). Antes o vídeo saía a 2,5 Mbps — uns 19MB por minuto, mais
// o que o VBR estoura em tela com movimento —, então perto dos 2 minutos o
// arquivo passava do teto e o upload falhava depois de tudo gravado.
export const LIMITE_DO_BUCKET_BYTES = 50 * 1024 * 1024;

// A gravação para sozinha antes do teto. A folga cobre o último chunk (que
// ainda chega depois do stop) e a variação do VBR.
export const TETO_DA_GRAVACAO_BYTES = 46 * 1024 * 1024;

// Quem grava escolhe entre nitidez e duração. Tela é conteúdo quase parado
// (texto, janelas), então VP9 aguenta taxas baixas; o que se perde primeiro é
// letra miúda (720p reduz a tela) e fluidez ao rolar (fps baixo).
//
// A nitidez de cada quadro vem dos bits por pixel (taxa ÷ largura × altura ×
// fps), não da taxa sozinha: a Alta já foi 1080p/30fps a 1 Mbps e saía mais
// borrada que a Econômica. Cada perfil precisa ter mais bits por pixel que o
// de baixo.
export type QualidadeDeVideo = 'alta' | 'media' | 'economica';

export interface PerfilDeQualidade {
  rotulo: string;
  descricao: string;
  largura: number;
  altura: number;
  fps: number;
  bitrateDeVideo: number;
  bitrateDeAudio: number;
}

export const QUALIDADES: Record<QualidadeDeVideo, PerfilDeQualidade> = {
  alta: {
    rotulo: 'Alta',
    descricao: 'Texto miúdo nítido (planilhas, código)',
    largura: 1920,
    altura: 1080,
    fps: 15,
    bitrateDeVideo: 1_500_000,
    bitrateDeAudio: 96_000
  },
  media: {
    rotulo: 'Média',
    descricao: 'Bom para mostrar telas e fluxos',
    largura: 1280,
    altura: 720,
    fps: 15,
    bitrateDeVideo: 500_000,
    bitrateDeAudio: 96_000
  },
  economica: {
    rotulo: 'Econômica',
    descricao: 'Gravações longas; rolagem fica picotada',
    largura: 1280,
    altura: 720,
    fps: 10,
    bitrateDeVideo: 250_000,
    bitrateDeAudio: 64_000
  }
};

export const QUALIDADE_PADRAO: QualidadeDeVideo = 'media';

// Ordem de preferência. VP9 comprime tela bem melhor que VP8; `video/mp4`
// existe porque o Safari não grava webm. Todo container desta lista precisa
// estar em `allowed_mime_types` do bucket.
export const FORMATOS_DE_VIDEO_PREFERIDOS = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4'
];

export function escolherMimeTypeDeVideo(suportado: (tipo: string) => boolean): string | null {
  return FORMATOS_DE_VIDEO_PREFERIDOS.find(tipo => suportado(tipo)) ?? null;
}

export function extensaoDoMimeDeVideo(mime: string): string {
  return containerDoMime(mime) === 'video/mp4' ? 'mp4' : 'webm';
}

export function nomeDoArquivoDeVideo(mime: string, agora: Date): string {
  // Sem dois pontos: o nome entra no caminho do objeto no Storage.
  const instante = agora.toISOString().replace(/\.\d+Z$/, '').replace(/:/g, '-');
  return `screen-recording-${instante}.${extensaoDoMimeDeVideo(mime)}`;
}

export function atingiuTetoDeTamanho(bytes: number): boolean {
  return bytes >= TETO_DA_GRAVACAO_BYTES;
}

// Duração aproximada que cabe no teto — só para orientar quem grava.
export function minutosAproximadosNoTeto(qualidade: QualidadeDeVideo): number {
  const { bitrateDeVideo, bitrateDeAudio } = QUALIDADES[qualidade];
  const bytesPorSegundo = (bitrateDeVideo + bitrateDeAudio) / 8;
  return Math.floor(TETO_DA_GRAVACAO_BYTES / bytesPorSegundo / 60);
}
